//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import axios from './axios';
import { buildAuthHeaders } from './authHeaders';
import {
  IntegrationPreferences,
  DefaultIntegrationSelection,
  UpdateDefaultIntegrationSelectionRequest,
} from '../types/integrationPreferences';

/**
 * The slice of the OIDC user this client actually needs.
 *
 * Callers commonly pass `() => ({ access_token: user.access_token })` rather than the whole
 * `User`, so requiring the full type would force an `any` cast at every call site and lose
 * checking on the one field that matters.
 */
export type UserContextProvider = () => { access_token?: string } | undefined;

/** Path segment for the current-user preference endpoints. */
export const PREFERENCES_PATH = 'users/me/integration-preferences';
export const DEFAULT_INTEGRATION_PATH = 'users/me/default-integration';

/**
 * Client for the multi-group integration preference endpoints.
 *
 * Follows the singleton shape of UserGroupDataService so both share the teamspace base URL
 * and OIDC token handling. The endpoints are current-user scoped — the server derives the
 * user from the token, so no user id is ever sent.
 */
class IntegrationPreferencesService {
  private static instance: IntegrationPreferencesService;
  private tenantBaseUrl: string;
  private getUserContext: UserContextProvider;

  private constructor(
    tenantBaseUrl: string,
    getUserContext: UserContextProvider,
  ) {
    this.tenantBaseUrl = tenantBaseUrl;
    this.getUserContext = getUserContext;
  }

  public static getInstance(
    tenantBaseUrl?: string,
    getUserContext?: UserContextProvider,
  ): IntegrationPreferencesService {
    if (
      !IntegrationPreferencesService.instance &&
      tenantBaseUrl &&
      getUserContext
    ) {
      IntegrationPreferencesService.instance =
        new IntegrationPreferencesService(tenantBaseUrl, getUserContext);
    } else if (
      IntegrationPreferencesService.instance &&
      tenantBaseUrl &&
      getUserContext
    ) {
      // Re-initialize if URL or context changed (e.g., after logout/login)
      if (
        IntegrationPreferencesService.instance.tenantBaseUrl !==
          tenantBaseUrl ||
        IntegrationPreferencesService.instance.getUserContext !== getUserContext
      ) {
        IntegrationPreferencesService.instance =
          new IntegrationPreferencesService(tenantBaseUrl, getUserContext);
      }
    }
    return IntegrationPreferencesService.instance;
  }

  public static resetInstance(): void {
    IntegrationPreferencesService.instance = null as any;
  }

  private getAuthHeaders(): { Authorization: string; 'Content-Type': string } {
    return buildAuthHeaders(this.getUserContext);
  }

  /**
   * Every integration the current user can reach in this teamspace, with the group that
   * grants each one.
   *
   * A 204 means no group grants an enabled integration. Under group-only access that is a
   * routine state rather than an error, so it is normalised to an empty option list with
   * `NO_ACCESS` instead of being thrown.
   */
  async getPreferences(teamspaceId: string): Promise<IntegrationPreferences> {
    try {
      const response = await axios.get<IntegrationPreferences>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/${PREFERENCES_PATH}`,
        { headers: this.getAuthHeaders() },
      );
      if (response.status === 204) {
        return {
          options: [],
          requiresSelection: false,
          fallbackSource: 'NO_ACCESS',
        };
      }
      return response.data;
    } catch (error: any) {
      console.error('Error fetching integration preferences:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to fetch integration preferences',
      );
    }
  }

  /**
   * The saved default path, or null when the user has never chosen one.
   *
   * A saved selection may come back with `valid: false` — the group was left, reassigned,
   * or the integration disabled. That is reported rather than hidden so the UI can explain
   * why the user is being asked to choose again.
   */
  async getDefaultIntegration(
    teamspaceId: string,
  ): Promise<DefaultIntegrationSelection | null> {
    try {
      const response = await axios.get<DefaultIntegrationSelection>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/${DEFAULT_INTEGRATION_PATH}`,
        { headers: this.getAuthHeaders() },
      );
      if (response.status === 204) {
        return null;
      }
      return response.data;
    } catch (error: any) {
      console.error('Error fetching default integration selection:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to fetch default integration selection',
      );
    }
  }

  /**
   * Save or replace the default path.
   *
   * Both the group and the integration name are sent: two groups can grant the same
   * integration, so the group is what disambiguates which configuration and which override
   * policy apply.
   */
  async setDefaultIntegration(
    teamspaceId: string,
    request: UpdateDefaultIntegrationSelectionRequest,
  ): Promise<DefaultIntegrationSelection> {
    try {
      const response = await axios.post<DefaultIntegrationSelection>(
        `${this.tenantBaseUrl}/teamspaces/${teamspaceId}/${DEFAULT_INTEGRATION_PATH}`,
        request,
        { headers: this.getAuthHeaders() },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error saving default integration selection:', error);
      throw new Error(
        error.response?.data?.message ||
          'Failed to save default integration selection',
      );
    }
  }
}

export default IntegrationPreferencesService;
